import React from 'react';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import {
  ModalFloatingCloseBtn,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useCustomer } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';

import { AddressForm } from '../../components/Address';
import { useAddressHandlers } from '../../components/Address/Address.hooks';
import { LoadingAnimation } from '../../components/LoadingAnimation';
import { NotFoundView } from '../../components/NotFoundView';
import { PageWrapper } from '../../components/PageWrapper';
import { TransparentModal } from '../../components/TransparentModal';
import type { Address } from '../../graphql/types';
import type {
  AddressEditScreenProps,
  ModalStackParamList,
} from '../../navigation/AppNavigation.props';

export const AddressEditScreen = ({
  navigation,
}: AddressEditScreenProps): React.ReactElement => {
  const { t } = useLocalizationContext();

  const handleGoBack = React.useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.getParent()?.goBack();
    }
  }, [navigation]);

  const { isMobile } = useBreakpointHelpers();

  const {
    params: { addressId },
  } = useRoute<RouteProp<ModalStackParamList, 'AddressEdit'>>();
  const {
    customer: { addresses },
    isCustomerLoaded,
  } = useCustomer();

  const {
    updateAddressResponse: { fetching: isUpdatingAddress },
    deleteAddressResponse: { fetching: isDeletingAddress },
    handleOnSave,
    handleOnClose,
    handleOnEdit,
    handleOnDelete,
  } = useAddressHandlers({
    addressToEdit: addressId,
    checkExistingBag: true,
    onClose: handleGoBack,
  });

  const address = addresses?.find((item: Address) => item.id === addressId);

  if (!isCustomerLoaded) return <LoadingAnimation />;

  if (isCustomerLoaded && !address) return <NotFoundView />;

  return (
    <TransparentModal size="small" onClose={handleOnClose}>
      {isMobile ? null : <ModalFloatingCloseBtn onPress={handleOnClose} />}
      <PageWrapper
        withHeaderTitle
        withoutFooter
        testID="account.edit-address.root.container"
      >
        <AddressForm
          address={address}
          visible={true}
          editing={true}
          loading={isUpdatingAddress}
          deleting={isDeletingAddress}
          noHeader={false}
          disableAddress={false}
          submitCta={t('account.addresses.save')}
          onSave={handleOnSave}
          onEdit={handleOnEdit}
          onDelete={handleOnDelete}
        />
      </PageWrapper>
    </TransparentModal>
  );
};
